<template>
  <div>
    <div class="bg-white py-6">
      <!-- Replace with your content -->
      <div>
        <div class="md:flex md:items-center md:justify-between sm:px-6 md:px-8">
          <div class="flex-1 min-w-0">
            <h2 class="text-2xl font-bold leading-7 text-gray-800 sm:text-3xl sm:truncate">
              {{ $t("messages.affiliateSummary.affiliateSummary") }}
            </h2>
          </div>
          <div class="mt-4 flex-shrink-0 flex md:mt-0 md:ml-4"></div>
        </div>
      </div>
    </div>
    <!-- /End replace -->

    <nav class="bg-white border-b border-t border-gray-200 flex" aria-label="Breadcrumb">
      <ol class="w-full mx-auto px-4 flex space-x-4 sm:px-6 md:px-8">
        <li class="flex">
          <div class="flex items-center">
            <a href="javascript:void(0)" class="text-gray-400 hover:text-gray-500">
              <HomeIcon class="flex-shrink-0 h-5 w-5" aria-hidden="true" />
              <span class="sr-only">Home</span>
            </a>
          </div>
        </li>
        <li v-for="page in pages" :key="page.name" class="flex">
          <div class="flex items-center">
            <svg class="flex-shrink-0 w-6 h-full text-gray-200" viewBox="0 0 24 44" preserveAspectRatio="none"
              fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
            </svg>
            <router-link :to="page.href" class="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700"
              :aria-current="page.current ? 'page' : undefined">{{ page.name }}</router-link>
          </div>
        </li>
      </ol>
    </nav>
    <div>
      <div class="space-y-8 divide-y divide-gray-200 bg-white py-3 border-b text-center">
        <div class="flex items-center md:justify-between justify-center md:mx-0">
          <div class="flex-1 px-2 flex md:flex-row flex-col flex-wrap justify-center lg:ml-6 lg:justify-start">
            <div class="max-w-lg w-full md:w-56 lg:max-w-xs">
              <label for="search" class="sr-only">Search</label>
              <div class="relative text-gray-400 focus-within:text-gray-600">
                <div class="pointer-events-none absolute inset-y-0 pl-3 left-0 flex items-center">
                  <SearchIcon class="h-5 w-5" aria-hidden="true" />
                </div>
                <input id="search" v-model="searchInput" maxlength="10" @keyup="searchText($event)"
                  @input="searchText($event)"
                  class="block w-full bg-white py-2 pl-10 focus:bg-white pr-3 border border-transparent rounded-md leading-5 text-gray-900 placeholder-gray-500 focus:ring-offset-blue-600 focus:ring-white focus:border-white sm:text-sm"
                  :placeholder="$t('messages.affiliateSummary.search')" type="search" name="search" />
              </div>
            </div>
            <div class="px-5">
              <Listbox as="div" v-model="selected">
                <div class="relative">
                  <ListboxButton
                    class="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                    <span class="block truncate">{{ selected.label }}</span>
                    <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <SelectorIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </ListboxButton>

                  <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100"
                    leave-to-class="opacity-0">
                    <ListboxOptions
                      class="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                      <ListboxOption as="template" v-for="month in months" :key="month.label" :value="month"
                        @click="search(month)">
                        <li :class="[
                          'hover:text-white hover:bg-indigo-600 text-gray-900 cursor-default select-none relative py-2 pl-3 pr-9',
                        ]">
                          <span :class="[
                            selected.value == month.value
                              ? 'font-semibold'
                              : 'font-normal',
                            'block truncate text-left',
                          ]">
                            {{ month.label }}
                          </span>

                          <span v-if="selected.value == month.value" :class="[
                            'absolute inset-y-0 right-0 flex items-center pr-4 pl-5',
                          ]">
                            <CheckIcon class="h-5 w-5" aria-hidden="true" />
                          </span>
                        </li>
                      </ListboxOption>
                    </ListboxOptions>
                  </transition>
                </div>
              </Listbox>
            </div>
            <div class="max-w-lg w-full md:w-64 lg:max-w-xs mx-auto md:mx-0">
              <div class="relative text-gray-600 focus-within:text-gray-600">
                <div class="pointer-events-none absolute inset-y-0 pl-1 left-0 flex items-center">
                  {{ $t("messages.affiliateSummary.from") }}
                  :
                </div>
                <input id="from" v-model="fromDate" maxlength="10"
                  class="block w-full bg-white py-2 pl-14 focus:bg-white pr-3 border border-transparent rounded-md leading-5 text-gray-900 placeholder-gray-500 focus:ring-offset-blue-600 focus:ring-white focus:border-white sm:text-sm"
                  placeholder="From" type="date" name="from" />
              </div>
            </div>

            <div class="max-w-lg w-full md:w-64 lg:max-w-xs mx-auto md:mx-0">
              <div class="relative text-gray-600 focus-within:text-gray-600">
                <div class="pointer-events-none absolute inset-y-0 pl-1 left-0 flex items-center">
                  {{ $t("messages.affiliateSummary.to") }}:
                </div>
                <input id="to" v-model="toDate" maxlength="10"
                  class="block w-full bg-white py-2 pl-10 focus:bg-white pr-3 border border-transparent rounded-md leading-5 text-gray-900 placeholder-gray-500 focus:ring-offset-blue-600 focus:ring-white focus:border-white sm:text-sm"
                  placeholder="To" type="date" name="to" />
                </div>
              </div>
              <button
              class="group relative w-auto flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md items-center gap-1 text-white"
              style="background-color: #3c82f6" type="submit" @click='refetch()'>
              <span>
              {{ $t("messages.affiliateSummary.search") }}
              </span>
              <font-awesome-icon v-show="isLoading" :icon="['fas', 'circle-notch']" class="text-white" spin />
              </button>
            </div>
        </div>
      </div>
    </div>
    <!-- <div class="px-8 py-6" v-show="!isLoaded">
      <div
        class="space-y-8 divide-y divide-gray-200 bg-white px-10 py-9 rounded-md shadow-md text-center"
      >
        <font-awesome-icon
          class="text-gray-500"
          :icon="['fas', 'circle-notch']"
          transform="grow-6"
          spin
        />
      </div>
    </div> -->

    <div class="py-6">
      <div
        class="px-9 md:flex md:flex-row md:flex-wrap md:flex-1 md:justify-evenly grid grid-cols-2 gap-2 gap-y-4 place-items-center">
        <div class="w-full md:w-1/5 min-h-full">
          <div class="shadow p-3 bg-blue-100 rounded-md h-24 md:h-32 xl:h-auto">
            <div class="font-bold text-lg">
              {{
                grandTotalData.total_user.toLocaleString("en", {
                  useGrouping: true,
                })
              }}
            </div>
            <div class="text-sm">
              {{ $t("messages.affiliateSummary.totalAffiliateUsers") }}
            </div>
          </div>
        </div>
        <div class="w-full md:w-1/5 min-h-full">
          <div class="shadow p-3 bg-indigo-100 rounded-md h-24 md:h-32 xl:h-auto">
            <div class="font-bold text-lg">
              {{
                parseFloat(grandTotalData.win_loss / 100).toLocaleString("en", {
                  useGrouping: true,
                  minimumFractionDigits: 2,
                })
              }}
            </div>
            <div class="text-sm">
              {{ $t("messages.affiliateSummary.totalAffiliateWinLoss") }}
            </div>
          </div>
        </div>
        <div class="w-full md:w-1/5 min-h-full">
          <div class="shadow p-3 bg-yellow-100 rounded-md h-24 md:h-32 xl:h-auto">
            <div class="font-bold text-lg">
              {{
                parseFloat(
                  grandTotalData.total_bet_amount / 100
                ).toLocaleString("en", {
                  useGrouping: true,
                  minimumFractionDigits: 2,
                })
              }}
            </div>
            <div class="text-sm">
              {{ $t("messages.affiliateSummary.totalAffiliateBetAmount") }}
            </div>
          </div>
        </div>
        <div class="w-full md:w-1/5 min-h-full">
          <div class="shadow p-3 bg-green-100 rounded-md h-24 md:h-32 xl:h-auto">
            <div class="font-bold text-lg">
              {{
                parseFloat(
                  grandTotalData.affiliate_amount / 100
                ).toLocaleString("en", {
                  useGrouping: true,
                  minimumFractionDigits: 2,
                })
              }}
            </div>
            <div class="text-sm">
              {{ $t("messages.affiliateSummary.totalAffiliateAmount") }}
            </div>
          </div>
        </div>
      </div>

      <div class="relative sm:px-6 md:px-8">
        <div class="bg-white shadow sm:rounded-md mt-6">
          <div class="shadow overflow-x-auto border-b border-gray-200 sm:rounded-lg">
            <div v-if="!isLoaded"
              class="absolute inset-0 bg-gray-100 bg-opacity-75 flex justify-center items-center z-50">
              <div class="loader"></div>
              <p class="text-gray-700 text-lg">Loading...</p>
            </div>
            <table class="table min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <!-- <th scope="col" @click="doSort('account_name')" -->
                  <th scope="col" @click="sortAccountName"
                    class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                    {{ $t("messages.affiliateSummary.accountName") }}
                  </th>
                  <th scope="col" @click="sortUsername"
                    class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center cursor-pointer">
                    {{ $t("messages.affiliateSummary.username") }}
                  </th>
                  <th scope="col" @click="sortBetAmount"
                    class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-right cursor-pointer">
                    {{ $t("messages.affiliateSummary.affiliateBetAmount") }}
                  </th>
                  <th scope="col" @click="sortAffiliateAmount"
                    class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-right cursor-pointer">
                    {{ $t("messages.affiliateSummary.affiliateAmount") }}
                  </th>
                  <th scope="col"
                    class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="data in dataRows" :key="data.user_id" class="hover:bg-gray-50">
                  <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500" style="color: #4169e1">
                    {{ data.account_name }}
                  </td>
                  <td @click="viewData(data)"
                    class="px-3 py-4 whitespace-nowrap text-sm text-gray-500 text-center cursor-pointer">
                    {{ data.username }}
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    <p v-if="
                      data.summaries_data && data.summaries_data.bet_amount
                    " :style="parseFloat(data.summaries_data.bet_amount / 100) >= 0
                          ? { color: 'green' }
                          : { color: 'red' }
                        ">
                      {{
                        parseFloat(
                          data.summaries_data.bet_amount / 100
                        ).toLocaleString("en", {
                          useGrouping: true,
                          minimumFractionDigits: 2,
                        })
                      }}
                    </p>
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    <p v-if="
                      data.summaries_data
                      // &&
                      // data.summaries_data.affiliate_amount
                    " :style="parseFloat(
                        data.summaries_data.affiliate_amount / 100
                      ) >= 0
                          ? { color: 'green' }
                          : { color: 'red' }
                        ">
                      {{
                        data.summaries_data.affiliate_amount ?
                          parseFloat(
                            data.summaries_data.affiliate_amount / 100
                          ).toLocaleString("en", {
                            useGrouping: true,
                            minimumFractionDigits: 2,
                          }) : 0
                      }}
                    </p>
                  </td>
                  <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    <button type="button" href="javascript:void(0)"
                      @click="viewAffiliateDetail(data.summaries_data.data)"
                      class="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-blue-600">
                      View Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <Listbox as="div" v-model="rowsPerPage">
                    <ListboxButton
                      class="bg-white relative w-full border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                      <span class="block truncate">{{
                        rowsPerPage.value
                      }}</span>
                      <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <SelectorIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </span>
                    </ListboxButton>
                    <ListboxOptions
                      class="absolute z-10 mt-1 w-44 bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                      <ListboxOption as="template" v-for="(option, index) in rowsPerPageOption" :key="index"
                        :value="option" @click="refetch">
                        <li :class="[
                          'hover:text-white hover:bg-indigo-600 text-gray-900 cursor-default select-none relative py-2 pl-3 pr-9',
                        ]">
                          <span :class="[
                            option.value == rowsPerPageOption.value
                              ? 'font-semibold'
                              : 'font-normal',
                            'block truncate text-left',
                          ]">
                            {{ option.label }}
                          </span>

                          <span v-if="option.value == rowsPerPageOption.value" :class="[
                            'absolute inset-y-0 right-0 flex items-center pr-4 pl-5',
                          ]">
                            <CheckIcon class="h-5 w-5" aria-hidden="true" />
                          </span>
                        </li>
                      </ListboxOption>
                    </ListboxOptions>
                  </Listbox>
                  <!-- <p class="text-sm text-gray-700">
                    {{ $t("messages.affiliateSummary.showing") }}

                    <span class="font-medium"> {{ offset + 1 }}</span>
                    {{ " " }}
                    to
                    {{ " " }}
                    <span class="font-medium">
                      {{ offset + dataRows.length }}</span
                    >
                    {{ " " }}
                    of
                    {{ " " }}
                    <span class="font-medium">{{ dataCount }}</span>
                    {{ " " }}
                    {{ $t("messages.affiliateSummary.results") }}
                  </p> -->
                </div>
                <div>
                  <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <a href="javascript:void(0)" @click="jump(1)"
                      class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                      <span class="sr-only">First</span>
                      <span class="h-5 w-5 text-center">
                        <font-awesome-icon class="text-gray-500" :icon="['fas', 'chevron-double-left']"
                          transform="shrink-2" />
                      </span>
                    </a>
                    <a href="javascript:void(0)" @click="prev"
                      class="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                      <span class="sr-only">Previous</span>
                      <span class="h-5 w-5 text-center">
                        <font-awesome-icon class="text-gray-500" :icon="['fas', 'chevron-left']" transform="shrink-2" />
                      </span>
                    </a>
                    <a href="javascript:void(0)" @click="jump(i)" v-for="i in pageCount()" :key="i" :class="currentPage === i
                        ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600 relative inline-block items-center py-2 w-10 text-center border text-sm font-medium'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 relative inline-block items-center py-2 w-10 text-center border text-sm font-medium'
                      " :style="{
                        display:
                          Math.abs(i - currentPage) < 2 || currentPage == 0
                            ? 'block'
                            : 'none',
                      }" style="cursor: pointer">
                      {{ i }}
                    </a>
                    <a href="javascript:void(0)" @click="next"
                      class="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
                      <span class="sr-only">Next</span>
                      <span class="h-5 w-5 text-center">
                        <font-awesome-icon class="text-gray-500" :icon="['fas', 'chevron-right']"
                          transform="shrink-2" />
                      </span>
                    </a>
                    <!-- <a
                      href="javascript:void(0)"
                      @click="jump(Math.ceil(dataCount / rowsPerPage.value))"
                      class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span class="sr-only">Last</span>
                      <span class="h-5 w-5 text-center">
                        <font-awesome-icon
                          class="text-gray-500"
                          :icon="['fas', 'chevron-double-right']"
                          transform="shrink-2"
                        />
                      </span>
                    </a> -->
                  </nav>
                </div>
              </div>
            </div>
            <div v-show="showDetails" class="fixed z-10 inset-0 overflow-y-auto">
              <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 transition-opacity">
                  <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;
                <div
                  class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle max-w-3xl sm:w-full sm:p-6"
                  role="dialog" aria-modal="true" aria-labelledby="modal-headline">
                  <div style="overflow: auto">
                    <div class="flex">
                      <h1 class="text-lg mb-3">
                        {{ $t("messages.affiliateSummary.affiliateDetails") }}
                      </h1>
                      <div class="absolute right-4 md:right-6" @click="showDetails = !showDetails">
                        X
                      </div>
                    </div>
                    <table class="table-auto border-collapse border border-gray-400 text-sm w-full">
                      <thead>
                        <tr>
                          <th class="text-left border border-gray-400 p-2">
                            {{ $t("messages.affiliateSummary.level") }}
                          </th>
                          <th class="text-right border border-gray-400 p-2">
                            {{ $t("messages.affiliateSummary.totalDeposit") }}
                          </th>
                          <th class="text-right border border-gray-400 p-2">
                            {{ $t("messages.affiliateSummary.betAmount") }}
                          </th>
                          <th class="text-right border border-gray-400 p-2">
                            {{ $t("messages.affiliateSummary.settleAmount") }}
                          </th>

                          <th class="text-right border border-gray-400 p-2">
                            {{ $t("messages.affiliateSummary.winloss") }}
                          </th>
                          <th class="text-right border border-gray-400 p-2">
                            {{
                              $t("messages.affiliateSummary.affiliateAmount")
                            }}
                          </th>

                          <th class="text-right border border-gray-400 p-2">
                            {{ $t("messages.affiliateSummary.withdrawAmount") }}
                          </th>
                        </tr>
                      </thead>
                      <tr v-if="details.lv1">
                        <td class="text-center border border-gray-400 p-2">
                          1
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          {{
                            parseFloat(
                              details.lv1.deposit / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          {{
                            parseFloat(
                              details.lv1.bet_amount / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          <p v-if="
                            details &&
                            details.lv1 &&
                            details.lv1.total_settle_amount >= 0
                          ">
                            {{
                              parseFloat(
                                details.lv1.total_settle_amount / 100
                              ).toLocaleString("en", {
                                useGrouping: true,
                                minimumFractionDigits: 2,
                              })
                            }}
                          </p>
                        </td>
                        <td class="text-right border border-gray-400 p-2" :style="parseFloat(details.lv1.win_loss / 100) < 0
                            ? 'color:red'
                            : 'color:green'
                          ">
                          {{
                            parseFloat(
                              details.lv1.win_loss / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          <p v-if="
                            details &&
                            details.lv1
                            // && details.lv1.affiliate_amount
                          ">
                            {{
                              details.lv1.affiliate_amount ? parseFloat(
                                details.lv1.affiliate_amount / 100
                              ).toLocaleString("en", {
                                useGrouping: true,
                                minimumFractionDigits: 2,
                              }) : 0
                            }}
                          </p>
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          {{
                            parseFloat(
                              details.lv1.withdraw / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                      </tr>
                      <tr v-if="details.lv2">
                        <td class="text-center border border-gray-400 p-2">
                          2
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          {{
                            parseFloat(
                              details.lv2.deposit / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          {{
                            parseFloat(
                              details.lv2.bet_amount / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          {{
                            parseFloat(
                              details.lv2.total_settle_amount / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                        <td class="text-right border border-gray-400 p-2" :style="parseFloat(details.lv2.win_loss / 100) < 0
                            ? 'color:red'
                            : 'color:green'
                          ">
                          {{
                            parseFloat(
                              details.lv2.win_loss / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          <p v-if="details.lv2">
                            {{
                              details.lv2.affiliate_amount ? parseFloat(
                                details.lv2.affiliate_amount / 100
                              ).toLocaleString("en", {
                                useGrouping: true,
                                minimumFractionDigits: 2,
                              }) : 0
                            }}
                          </p>
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          {{
                            parseFloat(
                              details.lv2.withdraw / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                      </tr>
                      <tr v-if="details.lv3">
                        <td class="text-center border border-gray-400 p-2">
                          3
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          {{
                            parseFloat(
                              details.lv3.deposit / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          {{
                            parseFloat(
                              details.lv3.bet_amount / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          {{
                            parseFloat(
                              details.lv3.total_settle_amount / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                        <td class="text-right border border-gray-400 p-2" :style="parseFloat(details.lv3.win_loss / 100) < 0
                            ? 'color:red'
                            : 'color:green'
                          ">
                          {{
                            parseFloat(
                              details.lv3.win_loss / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          <p v-if="details.lv3">
                            {{
                              details.lv3.affiliate_amount ? parseFloat(
                                details.lv3.affiliate_amount / 100
                              ).toLocaleString("en", {
                                useGrouping: true,
                                minimumFractionDigits: 2,
                              }) : 0
                            }}
                          </p>
                        </td>
                        <td class="text-right border border-gray-400 p-2">
                          {{
                            parseFloat(
                              details.lv3.withdraw / 100
                            ).toLocaleString("en", {
                              useGrouping: true,
                              minimumFractionDigits: 2,
                            })
                          }}
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MailIcon,
  HomeIcon,
  SearchIcon,
  CheckIcon,
  SelectorIcon,
} from "@heroicons/vue/solid";
import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";
import { inject, onMounted, ref } from "@vue/runtime-core";
import { useStore } from "vuex";
import * as dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { useRoute, useRouter } from "vue-router";

dayjs.extend(localizedFormat);

const pages = [{ name: "Affiliate Summary", href: "", current: true }];

const rowsPerPageOption = [
  { label: "2", value: 2 },
  { label: "5", value: 5 },
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "30", value: 30 },
  { label: "40", value: 40 },
];

export default {
  components: {
    ChevronLeftIcon,
    ChevronRightIcon,
    CheckCircleIcon,
    MailIcon,
    HomeIcon,
    SearchIcon,
    Listbox,
    ListboxButton,
    ListboxLabel,
    ListboxOption,
    ListboxOptions,
    CheckIcon,
    SelectorIcon,
  },
  setup() {
    const axios = inject("$axios");
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const searchInput = ref("");
    const fromDate = ref(dayjs().add(-1, "day").format("YYYY-MM-DD"));
    const toDate = ref(dayjs().format("YYYY-MM-DD"));
    const currentPage = ref(1);
    const offset = ref(0);
    const months = ref([]);

    // for (let i = 0; i <= diff; i++) {
    //   months.value.push({
    //     label: dayjs("2021-07-01").add(i, "month").format("MM/YYYY"),
    //     value: dayjs("2021-07-01").add(i, "month").format(),
    //   })
    // }

    const onMonthChange = (data) => {
      selectedOption.value = data;
      filterByMonth(data);
    };

    const filterByMonth = (payload) => {
      let from = dayjs(payload.value).startOf("month").format("YYYY-MM-DD");
      let to = dayjs(payload.value).endOf("month").format("YYYY-MM-DD");
      if (payload.label == "Yesterday") {
        from = dayjs(payload.value)
          .hour(0)
          .minute(0)
          .second(0)
          .format("YYYY-MM-DD");
        to = dayjs(payload.value)
          .hour(23)
          .minute(59)
          .second(59)
          .format("YYYY-MM-DD");
      }

      // console.log('dateSelectorRef', dateSelectorRef);
      // dateSelectorRef.value.state = { from: dayjs(from).format("YYYY-MM-DDThh:mm"), to: dayjs(to).format("YYYY-MM-DDThh:mm") }

      isLoaded.value = false;
      filterForm.value.from = from;
      filterForm.value.to = to;
      refetch();
      refetchTotalData();
    };

    const rowsPerPage = ref({ label: "10", value: 10 });
    let sortBy = ref([]);
    let totals = ref({
      newUser: 0,
      activeUser: 0,
      deposit: 0,
      totalBet: 0,
      betAmount: 0,
      validBetAmount: 0,
      winLoss: 0,
      winningPercentage: 0,
      withdraw: 0,
      withdrawPlayer: 0,
    });

    const startDate = ref(dayjs());
    const beginningDate = "2022-01-01";
    const diff = startDate.value.diff(beginningDate, "months");
    const showDetails = ref(false);
    let details = ref({});
    let sorted = ref([]);

    for (let i = 0; i <= diff; i++) {
      months.value.push({
        label: dayjs(beginningDate).add(i, "month").format("MM/YYYY"),
        value: dayjs(beginningDate).add(i, "month").format(),
      });
    }

    months.value.push({
      label: "Yesterday",
      value: dayjs().add(-1, "day").format("YYYY-MM-DD"),
    });
    const selected = ref({
      label: "Yesterday",
      value: dayjs().add(-1, "day").format("YYYY-MM-DD"),
    });

    // const selected = ref(months.value[months.value.length - 1])

    let dataRows = ref([]);
    let grandTotalData = ref({
      total_user: 0,
      win_loss: 0,
      total_bet_amount: 0,
      affiliate_amount: 0,
    });
    let dataCount = ref(0);
    let isLoaded = ref(false);

    onMounted(() => {
      refetch();
      refetchTotalData();
    });

    const setRows = ({ currentPage, rowsPerPage }) => {
      return {
        start: currentPage * rowsPerPage - rowsPerPage,
        end: currentPage * rowsPerPage - 1,
      };
    };

    const refetchTotalData = (start, end) => {
      let fromParams = fromDate
        ? fromDate.value
        : dayjs().add(-1, "day").format("YYYY-MM-DD");
      let toParams = toDate ? toDate.value : dayjs().format("YYYY-MM-DD");
      axios
        .post(
          process.env.VUE_APP_API_ENDPOINT +
          "/admin/api/v1/cms/summaries/menu/affiliate-report",
          {
            operator_id: store.state.selectedOperator.operatorId,
            start_date: start ? start : fromParams,
            end_date: end ? end : toParams,
          }
        )
        .then(({ data }) => {
          grandTotalData.value = data;
          isLoaded.value = true;

          // console.log({ rows: dataRows.value })
        })
        .catch(({ response }) => {
          console.log(response.data);
        });
    };

    const refetch = (sort_by = []) => {
      let fromParams = fromDate
        ? fromDate.value
        : dayjs().add(-1, "day").format("YYYY-MM-DD");
      let toParams = toDate ? toDate.value : dayjs().format("YYYY-MM-DD");
      sorted = sortBy.value.length > 0 ? sortBy.value : sort_by;
      isLoaded.value = false;

      axios
        .post(
          process.env.VUE_APP_API_ENDPOINT + "/admin/api/v1/cms/summaries/menu/affiliate",
          {
            operator_id: store.state.selectedOperator.operatorId,
            limit: rowsPerPage.value.value,
            search: searchInput.value,
            offset: offset.value,
            start_date: fromParams,
            end_date: toParams,
            sort_by: sorted,
          }
        )
        .then(({ data }) => {
          // console.log({ data })
          dataRows.value = data.rows;
          dataCount.value = data.rows.length;
          // dataCount.value = data.count;
          isLoaded.value = true;

          // console.log({ rows: dataRows.value })
        })
        .catch(({ response }) => {
          console.log(response.data);
        });
    };

    const searchText = (e) => {
      if (!fromDate) fromDate = dayjs().startOf("month").format("YYYY-MM-DD");

      if (!toDate) toDate = dayjs().format("YYYY-MM-DD");

      if (
        e.code == "Enter" ||
        e.code == "NumpadEnter" ||
        e.keyCode == 13 ||
        searchInput.value.length == 10
      ) {
        isLoaded.value = false;
        // console.log(searchInput.value)

        router.push({
          query: {
            search: searchInput.value,
            start_date: dayjs(fromDate.value).format("YYYY-MM-DD"),
            end_date: dayjs(toDate.value).format("YYYY-MM-DD"),
          },
        });
        axios
          .post(
            process.env.VUE_APP_API_ENDPOINT + "/admin/api/v1/cms/summaries/menu/affiliate",
            {
              operator_id: store.state.selectedOperator.operatorId,
              limit: rowsPerPage.value.value,
              offset: offset.value,
              search: searchInput.value,
              start_date: dayjs(fromDate.value).format("YYYY-MM-DD"),
              end_date: dayjs(toDate.value).format("YYYY-MM-DD"),
              sort_by: sorted,
            }
          )
          .then(({ data }) => {
            // console.log({ data })
            dataRows.value = data.rows;
            dataCount.value = data.rows.length;
            // dataCount.value = data.count;
            isLoaded.value = true;
          })
          .catch((err) => {
            console.log(err);
            isLoaded.value = true;
          });
      } else if (searchInput.value.length == 0) {
        refetch();
      }
    };

    const doSort = (attName) => {
      if (sortBy.value[0] && sortBy.value[0][0] == attName) {
        sortBy.value[0][1] = sortBy.value[0][1] * -1;
      } else {
        sortBy.value = [[attName, -1]];
      }

      dataRows.value = dataRows.value.sort((a, b) => {
        let fa =
          attName == "bet_amount" || attName == "affiliate_amount"
            ? a["summaries_data"][attName]
            : a[attName],
          fb =
            attName == "bet_amount" || attName == "affiliate_amount"
              ? b["summaries_data"][attName]
              : b[attName];

        if (sortBy.value[0][1] == -1) {
          if (fa < fb) {
            return -1;
          }
          if (fa > fb) {
            return 1;
          }
        } else {
          if (fa > fb) {
            return -1;
          }
          if (fa < fb) {
            return 1;
          }
        }

        return 0;
      });

      isLoaded.value = true;
    };

    const isSortBetAmount = ref(false);
    const isSortAffiliateAmount = ref(false);
    const isSortUsername = ref(false);
    const isSortAccountName = ref(false);

    const sortBetAmount = () => {
      isSortBetAmount.value = !isSortBetAmount.value;

      if (isSortBetAmount.value) {
        sortBy.value = ["bet_amount", "ASC"]
        refetch();
        return;
      }
      sortBy.value = ["bet_amount", "DESC"]
      refetch();
    };

    const sortAffiliateAmount = () => {
      isSortAffiliateAmount.value = !isSortAffiliateAmount.value;
      if (isSortAffiliateAmount.value) {
        sortBy.value = ["affiliate_amount", "DESC"]
        refetch();
        return;
      }
      sortBy.value = ["affiliate_amount", "ASC"]
      refetch();
    };

    const sortUsername = () => {
      isSortUsername.value = !isSortUsername.value;

      if (isSortUsername.value) {
        sortBy.value = ["username", "ASC"]
        refetch();
        return;
      }
      sortBy.value = ["username", "DESC"]
      refetch();
    };

    const sortAccountName = () => {
      isSortAccountName.value = !isSortAccountName.value;

      if (isSortAccountName.value) {
        sortBy.value = ["account_name", "ASC"]
        refetch();
        return;
      }
      sortBy.value = ["account_name", "DESC"]
      refetch();
    };

    const viewAffiliateDetail = (data) => {
      // console.log(data)
      showDetails.value = true;
      details.value = data;
    };

    const viewData = (data) => {
      router.push({ name: "Wallet Info", params: { id: data.user_id } });
    };
    
    const searchByDateRange = () => {
      if (!fromDate) fromDate = dayjs().startOf("month").format("YYYY-MM-DD");
      router.push({
        query: {
          search: searchInput.value,
          start_date: dayjs(fromDate.value).format("YYYY-MM-DD"),
          end_date: dayjs(toDate.value).format("YYYY-MM-DD"),
        },
      });
      currentPage.value = 1;
      isLoaded.value = false;

      axios
        .post(
          process.env.VUE_APP_API_ENDPOINT + "/admin/api/v1/cms/summaries/menu/affiliate",
          {
            operator_id: store.state.selectedOperator.operatorId,
            limit: rowsPerPage.value.value,
            offset: offset.value,
            search: searchInput.value,
            start_date: dayjs(fromDate.value).format("YYYY-MM-DD"),
            end_date: dayjs(toDate.value).format("YYYY-MM-DD"),
            sort_by: sorted.value,
          }
        )
        .then(({ data }) => {
          dataRows.value = data.rows;
          dataCount.value = data.rows.length;
          isLoaded.value = true;

          refetchTotalData(
            dayjs(fromDate.value).format("YYYY-MM-DD"),
            dayjs(toDate.value).format("YYYY-MM-DD")
          );
        })
        .catch((err) => {
          console.log(err);
          isLoaded.value = true;
        });
    };

    const search = (month) => {
      // console.log("vola")
      startDate.value = dayjs(month.value);
      let from = dayjs(month.value);
      let fromParams = dayjs(month.value).startOf("month").format("YYYY-MM-DD");
      let toParams = dayjs(month.value).endOf("month").format("YYYY-MM-DD");

      currentPage.value = 1;

      if (month.label == "Yesterday") {
        fromParams = dayjs(month.value).format("YYYY-MM-DD");
        toParams = dayjs(month.value).format("YYYY-MM-DD");
      }

      fromDate.value = fromParams;
      toDate.value = toParams;

      if (from.isValid()) {
        isLoaded.value = false;
        // console.log(store.state.selectedOperator.operatorId)
        axios
          .post(
            process.env.VUE_APP_API_ENDPOINT + "/admin/api/v1/cms/summaries/menu/affiliate",
            {
              search: searchInput.value,
              operator_id: store.state.selectedOperator.operatorId,
              limit: 10,
              offset: (parseInt(route.query.page) - 1 || 0) * 10,
              start_date: fromParams,
              end_date: toParams,
              sort_by: sorted,
            }
          )
          .then(({ data }) => {
            // console.log({ data })
            dataRows.value = data.rows;
            dataCount.value = data.rows.length;
            // dataCount.value = data.count;
            isLoaded.value = true;
            refetchTotalData(fromParams, toParams);
          })
          .catch((err) => {
            console.log(err);
            isLoaded.value = true;
          });
      }
    };

    const pageCount = (i) => {
      dataCount.value == rowsPerPage.value.value ? currentPage.value + 1 : currentPage.value
      // return Math.ceil(dataCount.value / rowsPerPage.value.value);
    };

    const paginationOffset = (i) => {
      return (parseInt(route.query.page) || 1) < 4
        ? i
        : parseInt(route.query.page) > Math.floor(dataCount.value / 10) - 3
          ? Math.floor(dataCount.value / 10) - 6 + i
          : route.query.page - 4 + i;
    };

    const prev = () => {
      if (currentPage.value - 1 >= 1) {
        currentPage.value -= 1;

        offset.value = (currentPage.value - 1) * rowsPerPage.value.value;
        refetch();
      }
    };

    const next = () => {
      // console.log('currentPage',currentPage.value);
      // console.log('rowsPerPage',rowsPerPage.value.value);
      // console.log('pageCount',pageCount());
      if (dataCount.value == rowsPerPage.value.value) {
        // if (currentPage.value + 1 <= pageCount()) {
        offset.value = rowsPerPage.value.value * currentPage.value;
        refetch();
        currentPage.value += 1;
      }
    };

    const jump = (page) => {
      currentPage.value = page;
      offset.value = (page - 1) * rowsPerPage.value.value;
      refetch();
    };

    return {
      dataRows,
      dataCount,
      totals,
      isLoaded,
      dayjs,
      selected,
      fromDate,
      toDate,
      route,
      prev,
      next,
      search,
      searchByDateRange,
      viewData,
      months,
      jump,
      pageCount,
      paginationOffset,
      viewAffiliateDetail,
      showDetails,
      details,
      searchText,
      searchInput,
      pages,
      doSort,
      refetch,
      rowsPerPageOption,
      rowsPerPage,
      currentPage,
      offset,
      setRows,
      sortBetAmount,
      sortAffiliateAmount,
      onMonthChange,
      grandTotalData,
      sortUsername,
      sortAccountName,
    };
  },
};
</script>
<style scoped>
/* Add a simple spinner style without a background color */
.loader {
  border: 4px solid rgba(0, 0, 0, 0.1); 
  border-radius: 50%;
  border-top: 4px solid transparent; 
  width: 36px;
  height: 36px;
  -webkit-animation: spin 2s linear infinite;
  animation: spin 2s linear infinite;
}

@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }

  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>